import classes from "./Autocomplete.module.scss";
import { createAutocomplete } from "@algolia/autocomplete-core";
import { AutocompleteState } from "@algolia/autocomplete-js";
import React, { FC, useEffect, useRef, useState } from "react";
import { AutocompleteItem } from "../AutocompleteItem/AutocompleteItem";
import { detect } from "detect-browser";

import { querySuggestionsPluginInCategory } from "../../helpers/search";

import { ReactComponent as SearchGreenIcon } from "../../assets/icons/search-green.svg";
import { ReactComponent as SearchGreyIcon } from "../../assets/icons/search-grey.svg";
import { ReactComponent as CloseIcon } from "../../assets/icons/close.svg";
import { modifyAutocompleteSearchResults } from "../../helpers/autocomplete";
import { searchStateToUrl } from "../../helpers/search";
import { useConnector } from "react-instantsearch";
import connectAutocomplete from "instantsearch.js/es/connectors/autocomplete/connectAutocomplete";
import { useInstantSearch, useRefinementList } from "react-instantsearch";

import type {
  AutocompleteConnectorParams,
  AutocompleteWidgetDescription,
} from "instantsearch.js/es/connectors/autocomplete/connectAutocomplete";

type Props = {};

interface AutocompleteItemProps extends JobDescription {
  nb_world: number;
  popularity: number;
  query: string;
  objectID: string;
}

export type UseAutocompleteProps = AutocompleteConnectorParams;

export function useAutocomplete(props?: UseAutocompleteProps) {
  return useConnector<
    AutocompleteConnectorParams,
    AutocompleteWidgetDescription
  >(connectAutocomplete, props);
}

export const Autocomplete: FC<any> = ({
  inputRef,
  placeholder,
  jobCategories,
  autocompleteRef,
  isFormSubmitted,
  setIsFormSubmitted,
  ...props
}) => {
  const { indexUiState } = useInstantSearch();
  const [autocompleteQuery, setAutocompleteQuery] = useState<string>("");
  const { refine } = useAutocomplete(props);

  useEffect(() => {
    const isSearchStateNotEmpty = Object.values(indexUiState).some((v) => v);
    if (isSearchStateNotEmpty) {
      setIsFormSubmitted(true);
    }
  }, [isFormSubmitted]);
  const formRef = useRef<HTMLFormElement>(null);
  const cancelRef = useRef<HTMLDivElement>(null);
  const [isMounted, setIsMounted] = useState<boolean>(false);
  const [isFocused, setIsFocused] = useState<boolean>(false);
  const [noBlurBackground, setNoBlurBackground] = useState<boolean>(false);

  useEffect(() => {
    if (indexUiState?.query) {
      setAutocompleteQuery(indexUiState.query as string);
    }
  }, []);

  const [autocompleteState, setAutocompleteState] = useState<
    AutocompleteState<any>
  >({
    activeItemId: null,
    collections: [],
    completion: null,
    context: {},
    isOpen: false,
    query: "",
    status: "idle",
  });

  useEffect(() => {
    refine(autocompleteQuery);
  }, [autocompleteQuery]);

  const [items, setItems] = useState<AutocompleteItemProps[]>([]);

  const autocomplete = React.useMemo(
    () =>
      createAutocomplete({
        onStateChange({ state }) {
          // @ts-ignore
          setAutocompleteState(state);

          if (state.query.length > 0 && setIsFormSubmitted) {
            setIsFormSubmitted(true);
          }
        },
        onSubmit({ state }) {
          if (setIsFormSubmitted) {
            setIsFormSubmitted(true);
          }
          if (setAutocompleteQuery) {
            setAutocompleteQuery(state.query);
          }
        },
        plugins: [querySuggestionsPluginInCategory],
        ...props,
      }),
    []
  );

  useEffect(() => {
    autocompleteRef.current = autocomplete;
    setIsMounted(true);

    if (detect()?.name === "firefox" || detect()?.name === "ie") {
      setNoBlurBackground(true);
    }

    return () => setIsMounted(false);
  }, []);

  useEffect(() => {
    if (autocompleteQuery) {
      autocomplete.setQuery(autocompleteQuery);
    }
  }, [autocompleteQuery]);

  const handleCancel = () => {
    autocomplete.setQuery("");
    inputRef.current?.focus();

    refine("");
  };

  useEffect(() => {
    if (jobCategories) {
      setItems(
        modifyAutocompleteSearchResults(
          autocompleteState?.collections,
          jobCategories
        ) as AutocompleteItemProps[]
      );
    }
  }, [jobCategories, autocompleteState.collections]);

  return isMounted ? (
    <div className={classes.autocomplete} {...autocomplete.getRootProps({})}>
      {/* @ts-ignore */}
      <form
        className={classes.autocompleteForm}
        {...autocomplete.getFormProps({ inputElement: inputRef.current })}
        ref={formRef}
      >
        <div className={classes.autocompleteSearchWrapper}>
          {autocompleteState.query.length > 0 ? (
            <SearchGreenIcon />
          ) : (
            <SearchGreyIcon />
          )}
        </div>

        <div className={classes.autocompleteInputWrapper}>
          {/* @ts-ignore */}
          <input
            className={`
              ${classes.autocompleteInput}
              ${autocompleteState.query.length > 0 ? classes.hasPadding : ""}
            `}
            ref={inputRef}
            {...autocomplete.getInputProps({
              inputElement: inputRef.current,
              placeholder,
              onInput(event: { target: HTMLInputElement }) {
                refine(event.target.value);
              },
            })}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
          />
        </div>
        <div
          ref={cancelRef}
          onClick={handleCancel}
          style={{
            display: autocompleteState.query.length > 0 ? "block" : "none",
          }}
          className={classes.autocompleteCancelWrapper}
        >
          {autocompleteState.query.length > 0 && <CloseIcon />}
        </div>
      </form>
        {/* @ts-ignore */}
        <div
          style={{ display: autocompleteState.isOpen && isFocused ? "block" : "none"}}
          className={classes.autocompletePanel}
          {...autocomplete.getPanelProps({})}
        >
          <div
            className={`
            ${classes.autocompleteSource}
            ${noBlurBackground ? classes.noBlur : ""}
          `}
          >
            <ul
              className={classes.autocompletePanelList}
              {...autocomplete.getListProps()}
            >
              {items.length > 0 &&
                items.map((item: any) => {
                  if (item.source.sourceId === "products") {
                    return (
                      <li
                        className={classes.autocompleteListItem}
                        key={item.objectID}
                      >
                        <AutocompleteItem
                          isJob
                          objectID={item.objectID}
                          group={item.group}
                          groupLabel={item.groupLabel}
                          query={autocompleteState.query}
                          setAutocompleteIsOpen={autocomplete.setIsOpen}
                          title={item.title}
                          {...autocomplete.getItemProps({
                            item,
                            source: item.source,
                          })}
                        />
                      </li>
                    );
                  }

                  return (
                    <li
                      onClick={() => {
                        if (setAutocompleteQuery) {
                          setAutocompleteQuery(item.query);
                          if (setIsFormSubmitted) {
                            setIsFormSubmitted(true);
                          }
                        }
                      }}
                      className={classes.autocompleteListItem}
                      key={item.objectID}
                    >
                      <AutocompleteItem
                        objectID={item.objectID}
                        query={item.query}
                        {...autocomplete.getItemProps({
                          item,
                          source: item.source,
                        })}
                      />
                    </li>
                  );
                })}
            </ul>
          </div>
        </div>
    </div>
  ) : (
    <></>
  );
};